/**
 * The vocabulary of a feedback item.
 *
 * Everything else in this module reads its words from here, so renaming a
 * state or adding one is a single edit rather than a search across screens.
 *
 * Two rules are worth stating out loud because they are what makes the
 * board honest rather than decorative:
 *
 *   - Nothing goes back to `new`. New means nobody has looked yet, and once
 *     somebody has, that is no longer true. Reopening lands on `triaged`.
 *   - Closing an item as `declined` or `duplicate` requires a reply. You do
 *     not get to say no without saying why; the submitter reads that line.
 *
 * Pure. No React, no network, no browser.
 */

/** What kind of thing is being reported. */
export const TYPE = {
  BUG: 'bug',
  IMPROVEMENT: 'improvement',
  FEATURE: 'feature',
};

export const TYPE_ORDER = [TYPE.BUG, TYPE.IMPROVEMENT, TYPE.FEATURE];

/**
 * `prompt` is what the submitter picks from -- written as the sentence they
 * would actually say. `label` is the one-word version the board shows.
 */
export const TYPE_META = {
  [TYPE.BUG]: {
    label: 'Bug',
    prompt: 'Something is broken',
    tone: 'red',
  },
  [TYPE.IMPROVEMENT]: {
    label: 'Improvement',
    prompt: 'Something works, but badly',
    tone: 'amber',
  },
  [TYPE.FEATURE]: {
    label: 'Feature',
    prompt: 'Something I need is missing',
    tone: 'blue',
  },
};

/**
 * How much the thing costs the person reporting it.
 *
 * This is the only severity field. Staff edit it during triage rather than
 * setting a second priority of their own -- two near-identical rankings in
 * one tracker is how both of them stop being trusted.
 */
export const IMPACT = {
  BLOCKING: 'blocking',
  SLOWS: 'slows_me_down',
  NICE: 'nice_to_have',
};

export const IMPACT_ORDER = [IMPACT.BLOCKING, IMPACT.SLOWS, IMPACT.NICE];

export const IMPACT_META = {
  [IMPACT.BLOCKING]: {
    label: 'Blocking',
    prompt: 'I cannot get the work done',
    rank: 0,
    tone: 'red',
  },
  [IMPACT.SLOWS]: {
    label: 'Slows me down',
    prompt: 'I have a workaround, it costs me time',
    rank: 1,
    tone: 'amber',
  },
  [IMPACT.NICE]: {
    label: 'Nice to have',
    prompt: 'It would be better, nothing is stuck',
    rank: 2,
    tone: 'grey',
  },
};

/** Where an item is in its life. */
export const STATE = {
  NEW: 'new',
  TRIAGED: 'triaged',
  PLANNED: 'planned',
  IN_PROGRESS: 'in_progress',
  SHIPPED: 'shipped',
  DECLINED: 'declined',
  DUPLICATE: 'duplicate',
};

export const STATE_ORDER = [
  STATE.NEW,
  STATE.TRIAGED,
  STATE.PLANNED,
  STATE.IN_PROGRESS,
  STATE.SHIPPED,
  STATE.DECLINED,
  STATE.DUPLICATE,
];

/** Still someone's problem. */
export const OPEN_STATES = [STATE.NEW, STATE.TRIAGED, STATE.PLANNED, STATE.IN_PROGRESS];

/** Finished, one way or another. */
export const CLOSED_STATES = [STATE.SHIPPED, STATE.DECLINED, STATE.DUPLICATE];

/**
 * `label` is for the board. `says` is the same state written for the person
 * who submitted it -- they do not care what column it sits in, they care
 * whether anything is going to happen.
 */
export const STATE_META = {
  [STATE.NEW]: {
    label: 'New',
    says: 'Waiting for the team to read it',
    tone: 'amber',
    live: true,
  },
  [STATE.TRIAGED]: {
    label: 'Triaged',
    says: 'Read and understood, not scheduled yet',
    tone: 'cyan',
  },
  [STATE.PLANNED]: {
    label: 'Planned',
    says: 'On the build list',
    tone: 'blue',
  },
  [STATE.IN_PROGRESS]: {
    label: 'In progress',
    says: 'Being built now',
    tone: 'violet',
    live: true,
  },
  [STATE.SHIPPED]: {
    label: 'Shipped',
    says: 'Live — go and have a look',
    tone: 'green',
  },
  [STATE.DECLINED]: {
    label: "Won't do",
    says: 'Not going ahead',
    tone: 'grey',
  },
  [STATE.DUPLICATE]: {
    label: 'Duplicate',
    says: 'Already tracked on another item',
    tone: 'grey',
  },
};

const UNKNOWN_STATE = { label: 'Unknown', says: 'Unrecognised status', tone: 'grey' };

const has = (list, value) => list.indexOf(value) !== -1;

export const isOpen = (state) => has(OPEN_STATES, state);
export const isClosed = (state) => has(CLOSED_STATES, state);

/** Never throws on an unrecognised state -- a new backend value renders grey. */
export function stateMeta(state) {
  return STATE_META[state] || { ...UNKNOWN_STATE, label: state || 'Unknown' };
}

export function typeMeta(type) {
  return TYPE_META[type] || { label: type || 'Other', prompt: '', tone: 'grey' };
}

export function impactMeta(impact) {
  return IMPACT_META[impact] || { label: impact || 'Unrated', prompt: '', rank: 3, tone: 'grey' };
}

/**
 * The states a staff member may move this item to.
 *
 * An open item can go anywhere else. A closed one can only be reopened, and
 * reopening lands on `triaged` because somebody has demonstrably read it.
 */
export function nextStates(state) {
  if (isClosed(state)) return [STATE.TRIAGED];
  return STATE_ORDER.filter((candidate) => candidate !== STATE.NEW && candidate !== state);
}

export function canTransition(from, to) {
  if (!to || from === to) return false;
  return has(nextStates(from), to);
}

/** Saying no, or saying "we already have that", owes the submitter a sentence. */
export function requiresReply(state) {
  return state === STATE.DECLINED || state === STATE.DUPLICATE;
}

/** A duplicate has to name what it duplicates, or it is just a silent close. */
export function requiresDuplicateOf(state) {
  return state === STATE.DUPLICATE;
}
